import React, { useCallback, useRef } from "react";

import { faBox, faFolder } from "@fortawesome/pro-regular-svg-icons";
import { useLocalStorage } from "usehooks-ts";

import { NO_FOLDER_FILTER } from "src/api";
import { useWorkspace } from "src/api/queries";
import { Card } from "src/base-components/Card";
import { DocumentTitle } from "src/base-components/DocumentTitle";
import { Spinner } from "src/base-components/Spinner";
import { Icon } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { FlowOverviewActionsHeader } from "src/flowsOverview/FlowOverviewActions";
import { FlowFolderList } from "src/flowsOverview/v2/FlowFolderList";
import { useFolders } from "src/flowsOverview/v2/folderQueries";
import { PageHeader } from "src/layout/PageHeader";
import { useFolderIdSearchParam } from "src/router/SearchParams";
import { useIsContentOverflows } from "src/utils/useIsContentOverflows";

export const GRID_COLUMNS_CLASS =
  `grid grid-cols-[repeat(auto-fill,_280px)] justify-center gap-4.5` as const;

const useFolderName = (workspaceId: string): string | null => {
  const [folderId] = useFolderIdSearchParam();
  const foldersResult = useFolders({ workspaceId });

  if (!folderId || folderId === NO_FOLDER_FILTER) {
    return null;
  }

  const folder = foldersResult.data?.folders.find((f) => f.id === folderId);
  return folder?.name ?? null;
};

/* Figma: https://www.figma.com/design/ha0mHhxPb4q2WRLzw0BcIK/Information-architecture-and-consistency?node-id=2890-9764&t=tllkNYmvY8vsQ0av-4 */
const FlowOverviewTitle: React.FC<{
  folderName: string | null;
  onFolderClick: () => void;
}> = ({ folderName, onFolderClick }) => {
  const decisionFlowSpanRef = useRef<HTMLSpanElement>(null);
  const folderNameSpanRef = useRef<HTMLSpanElement>(null);

  const isDFSpanOverflowing = useIsContentOverflows(decisionFlowSpanRef);
  const isFolderNameSpanOverflowing = useIsContentOverflows(folderNameSpanRef);

  let foldername = (
    <div
      className="-mr-1 flex min-w-[34px] shrink-1 cursor-pointer items-center gap-x-1.5 rounded-lg p-1 hover:bg-gray-50"
      onClick={onFolderClick}
    >
      <Icon icon={folderName ? faFolder : faBox} size="xs" variant="default" />
      <span
        className="min-w-4 truncate font-inter-semibold-13px text-gray-800"
        ref={folderNameSpanRef}
      >
        {folderName ?? "No folder"}
      </span>
    </div>
  );

  if (isFolderNameSpanOverflowing) {
    foldername = (
      <Tooltip title={folderName ?? "No folder"} asChild>
        {foldername}
      </Tooltip>
    );
  }

  let decisionFlows = (
    <span
      className="min-w-4 flex-1 truncate font-inter-semibold-13px text-gray-800"
      ref={decisionFlowSpanRef}
    >
      Decision Flows
    </span>
  );

  if (isDFSpanOverflowing) {
    decisionFlows = (
      <Tooltip title="Decision Flows" asChild>
        {decisionFlows}
      </Tooltip>
    );
  }

  return (
    <div className="flex items-center gap-x-1.5 overflow-hidden">
      {foldername}
      <span className="font-medium font-sans text-2xl text-gray-600">
        &#183;
      </span>
      {decisionFlows}
    </div>
  );
};

export const FlowOverview: React.FC<{
  organizationId: string;
  workspaceId: string;
}> = ({ organizationId, workspaceId }) => {
  const workspace = useWorkspace(workspaceId, { refetchOnPending: true });
  const folderName = useFolderName(workspaceId);
  const [, setSidebarCollapsed] = useLocalStorage(
    "mainSideBarNavigationCollapsed",
    false,
  );
  const [, setFoldersVisible] = useLocalStorage("sidebarFoldersVisible", false);

  const handleFolderClick = useCallback(() => {
    setSidebarCollapsed(false);
    setFoldersVisible(true);
  }, [setSidebarCollapsed, setFoldersVisible]);

  const isPending = workspace.data?.status === "PENDING";

  const title = (
    <FlowOverviewTitle
      folderName={folderName}
      onFolderClick={handleFolderClick}
    />
  );

  const headerSideContent = (
    <div className="h-full">
      <FlowOverviewActionsHeader
        organizationId={organizationId}
        workspaceId={workspaceId}
      />
    </div>
  );

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col">
      <DocumentTitle segments={["Decision Flows"]} />
      <PageHeader sideContent={headerSideContent} title={title} />
      <div className="flex min-h-0 w-full flex-1 flex-col overflow-y-auto">
        {!isPending && (
          <FlowFolderList
            organizationId={organizationId}
            workspaceId={workspaceId}
          />
        )}
        {isPending && (
          <div className="flex flex-col items-center">
            <Card className="flex h-80 w-2/3 flex-col items-center pt-2">
              <Spinner />
              <div className="mt-12 font-inter-semibold-16px">
                Workspace creation in progress...
              </div>
              <div className="mt-6 font-inter-normal-13px font-sans text-gray-800">
                Please wait for a few minutes while we set up your new
                workspace.
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};
